import React, { useRef, useState, useCallback } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RefreshCw } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

/**
 * Empty address object for resetting
 */
const EMPTY_ADDRESS = {
  street: '',
  apartment: '',
  city: '',
  state: '',
  zip_code: '',
  country: 'United States'
};

/**
 * Check if address has any data entered
 */
const hasAddressData = (address) => {
  return !!(
    address?.street ||
    address?.apartment ||
    address?.city ||
    address?.state ||
    address?.zip_code
  );
};

/**
 * AddressSection Component
 * 
 * A complete address entry section with:
 * - Google Places autocomplete for street address
 * - Individual editable fields for all address components
 * - Reset button to clear and re-enter address
 * - Smarty validation integration
 * - Consistent UI with LossPay design
 */
export default function AddressSection({
  address,
  onAddressChange,
  testIdPrefix = '',
  enableValidation = true,
  showApartment = true,
  required = true,
  disabled = false,
  className = ''
}) {
  const autocompleteRef = useRef(null);
  const [isResetting, setIsResetting] = useState(false);

  /**
   * Handle individual field changes
   */
  const handleFieldChange = useCallback((field, value) => {
    onAddressChange({
      ...address,
      [field]: value
    });
  }, [address, onAddressChange]);

  /**
   * Handle full address selection from autocomplete
   */
  const handleAddressSelect = useCallback((selectedAddress) => {
    onAddressChange({
      ...address,
      street: selectedAddress.street || address.street,
      apartment: selectedAddress.secondary || selectedAddress.apartment || address.apartment,
      city: selectedAddress.city || address.city,
      state: selectedAddress.state || address.state,
      zip_code: selectedAddress.zip_code || address.zip_code,
      country: selectedAddress.country || address.country
    });
  }, [address, onAddressChange]);

  /**
   * Reset all address fields
   */
  const handleReset = useCallback(() => {
    // Check if there's data to clear
    if (!hasAddressData(address)) {
      // No data to clear, just focus the input
      autocompleteRef.current?.focus();
      return;
    }

    setIsResetting(true);
    
    // Clear all address fields
    onAddressChange({ ...EMPTY_ADDRESS });
    
    // Reset the autocomplete component
    if (autocompleteRef.current) {
      autocompleteRef.current.reset();
    }
    
    // Brief visual feedback then reset state
    setTimeout(() => {
      setIsResetting(false);
    }, 300);
  }, [address, onAddressChange]);

  const hasData = hasAddressData(address);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Street Address with Reset Button */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label htmlFor={`${testIdPrefix}street`}>
            Street Address {required && '*'}
          </Label>
          {hasData && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={disabled || isResetting}
              className="h-7 px-2 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50"
              data-testid={`${testIdPrefix}reset-address-btn`}
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${isResetting ? 'animate-spin' : ''}`} />
              Reset Address
            </Button>
          )}
        </div>
        <AddressAutocomplete
          ref={autocompleteRef}
          id={`${testIdPrefix}street`}
          data-testid={`${testIdPrefix}street-input`}
          value={address.street}
          onChange={(e) => handleFieldChange('street', e.target.value)}
          onAddressSelect={handleAddressSelect}
          enableValidation={enableValidation}
          validateOnSelect={enableValidation}
          placeholder="Start typing address..."
          required={required}
          disabled={disabled}
        />
      </div>

      {/* Apartment/Unit */}
      {showApartment && (
        <div>
          <Label htmlFor={`${testIdPrefix}apartment`}>Apartment/Unit</Label>
          <Input
            id={`${testIdPrefix}apartment`}
            data-testid={`${testIdPrefix}apartment-input`}
            value={address.apartment}
            onChange={(e) => handleFieldChange('apartment', e.target.value)}
            placeholder="Apt, Suite, Unit, etc."
            disabled={disabled}
          />
        </div>
      )}

      {/* City, State, ZIP */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="col-span-2 md:col-span-2">
          <Label htmlFor={`${testIdPrefix}city`}>City {required && '*'}</Label>
          <Input
            id={`${testIdPrefix}city`}
            data-testid={`${testIdPrefix}city-input`}
            value={address.city}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            required={required}
            disabled={disabled}
          />
        </div>
        <div>
          <Label htmlFor={`${testIdPrefix}state`}>State {required && '*'}</Label>
          <Select 
            value={address.state}
            onValueChange={(value) => handleFieldChange('state', value)}
            disabled={disabled}
          >
            <SelectTrigger data-testid={`${testIdPrefix}state-select`}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.code} value={state.code}>
                  {state.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor={`${testIdPrefix}zip`}>ZIP Code {required && '*'}</Label>
          <Input
            id={`${testIdPrefix}zip`}
            data-testid={`${testIdPrefix}zip-input`}
            value={address.zip_code}
            onChange={(e) => handleFieldChange('zip_code', e.target.value)}
            maxLength={10}
            required={required}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}
